import type { CSSProperties, HTMLAttributes, ReactNode, Ref } from 'react';
import { cx } from '../lib/cx';
import { Button } from './Button';
import { ButtonControl, Tooltip } from './ButtonControl';
import { Icon } from './Icon';
import { Pill } from './Pill';

export type KalenderTone = 'anwesend' | 'fehlt' | 'nachgeholt' | 'kein_unterricht' | 'au' | 'anruf' | 'nachholung';

export interface KalenderColumn {
  key: string;
  label: string;
  weekday?: string;
  group?: string;
}

export interface KalenderCell {
  tone: KalenderTone;
  hint: string;
  time?: string | null;
  onOpen?: () => void;
}

const TONE_ICON: Partial<Record<KalenderTone, string>> = { fehlt: 'i-x', anruf: 'i-phone', nachholung: 'i-signature' };

const toneGlyph = (tone: KalenderTone): ReactNode => {
  if (tone === 'kein_unterricht') return <span className="calendar-tile-dot" />;
  if (tone === 'au') return "AU";
  return <Icon name={TONE_ICON[tone] ?? 'i-check'} />;
};

export function KalenderTile({ tone, hint, onOpen }: { tone: KalenderTone; hint: string; onOpen?: () => void }) {
  const className = `calendar-tile calendar-tile--${tone}`;
  if (onOpen) return <ButtonControl type="button" aria-label={hint} title={hint} className={cx(className, 'calendar-tile--button')} onClick={onOpen}>{toneGlyph(tone)}</ButtonControl>;
  return <Tooltip>
    <span role="img" aria-label={hint} title={hint} className={className}>{toneGlyph(tone)}</span>
  </Tooltip>;
}

function groupsOf(columns: readonly KalenderColumn[]): { label: string; span: number }[] {
  const groups: { label: string; span: number }[] = [];
  for (const { group } of columns) {
    if (group === undefined) continue;
    const last = groups[groups.length - 1];
    if (last?.label === group) last.span += 1;
    else groups.push({ label: group, span: 1 });
  }
  return groups;
}

export interface KalenderProps<R> extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  density: 'week' | 'month';
  inset?: boolean;
  rowLabel: string;
  totalLabel: string;
  columns: readonly KalenderColumn[];
  rows: readonly R[];
  rowKey: (row: R) => string;
  rowName: (row: R) => string;
  rowNote?: (row: R) => string | null;
  onRowOpen?: (row: R) => void;
  cell: (row: R, column: KalenderColumn) => KalenderCell | null;
  total: (row: R) => number;
  rowActions?: (row: R) => ReactNode;
}

export function Kalender<R>({
  ref, density, inset = false, rowLabel, totalLabel, columns, rows, rowKey, rowName, rowNote, onRowOpen, cell, total, rowActions, className, style, ...rest
}: KalenderProps<R> & { ref?: Ref<HTMLDivElement> }) {
  const groups = groupsOf(columns);
  const days = { '--calendar-days': columns.length } as CSSProperties;
  return (
    <div
      ref={ref}
      role="table"
      className={cx('calendar', density === 'month' && 'calendar--month', inset && 'calendar--inset', className)}
      style={{ ...style, ...days }}
      {...rest}
    >
      {groups.length > 0 && (
        <div role="row" className="calendar-row calendar-row--groups">
          <div role="presentation" />
          {groups.map((group) => (
            <div key={group.label} role="columnheader" className="calendar-group" style={{ gridColumn: `span ${group.span}` }}>{group.label}</div>
          ))}
          <div role="presentation" />
        </div>
      )}
      <div role="row" className="calendar-row calendar-row--head">
        <div role="columnheader" className="calendar-name">{rowLabel}</div>
        {columns.map((column) => (
          <div key={column.key} role="columnheader" className="calendar-head-day">
            {column.weekday !== undefined && <div className="calendar-head-weekday">{column.weekday}</div>}
            <div>{column.label}</div>
          </div>
        ))}
        <div role="columnheader" className="calendar-head-total">{totalLabel}</div>
      </div>
      {rows.map((row) => {
        const name = rowName(row);
        const note = rowNote?.(row);
        const count = total(row);
        return (
          <div key={rowKey(row)} role="row" className="calendar-row">
            <div role="rowheader" className="calendar-name">
              {onRowOpen
                ? <ButtonControl className="calendar-name-link calendar-name-text" onClick={() => onRowOpen(row)}>{name}</ButtonControl>
                : <span className="calendar-name-text">{name}</span>}
              {note && <div className="calendar-name-note">{note}</div>}
            </div>
            {columns.map((column) => {
              const value = cell(row, column);
              const tile = value && <KalenderTile tone={value.tone} hint={value.hint} onOpen={value.onOpen} />;
              return (
                <div key={column.key} role="cell" className="calendar-day">
                  {density === 'week'
                    ? <span className="calendar-slot">{tile}<span className="calendar-time">{value?.time}</span></span>
                    : tile}
                </div>
              );
            })}
            <div role="cell" className="calendar-day">
              <span className={cx('calendar-count', count > 0 && 'is-alert')}>{count}</span>
            </div>
            {rowActions && <div role="cell" className="calendar-row-actions">{rowActions(row)}</div>}
          </div>
        );
      })}
    </div>
  );
}

export function KalenderLegend({ items, inset = false }: { items: readonly { tone: KalenderTone; label: string }[]; inset?: boolean }) {
  return (
    <div className={cx('calendar-legend', inset && 'calendar-legend--inset')}>
      {items.map(({ tone, label }) => (
        <span key={tone} className="calendar-legend-item">
          <span aria-hidden="true" className={`calendar-tile calendar-tile--${tone}`}>{toneGlyph(tone)}</span>
          {label}
        </span>
      ))}
    </div>
  );
}

export function KalenderPeriod({ label, previousLabel, nextLabel, onPrevious, onNext }: {
  label: string;
  previousLabel: string;
  nextLabel: string;
  onPrevious: () => void;
  onNext: () => void;
}) {
  return (
    <Pill>
      <Button iconOnly aria-label={previousLabel} title={previousLabel} onClick={onPrevious}><Icon name="i-arrow-left" /></Button>
      <span className="calendar-period">{label}</span>
      <Button iconOnly aria-label={nextLabel} title={nextLabel} onClick={onNext}><Icon name="i-arrow-right" /></Button>
    </Pill>
  );
}

export function KalenderToggle<T extends string>({ options, value, onChange, 'aria-label': label }: {
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  'aria-label': string;
}) {
  return (
    <Pill role="group" aria-label={label} className="calendar-toggle">
      {options.map((option) => (
        <Button key={option.value} className="calendar-toggle-option" aria-pressed={option.value === value} onClick={() => onChange(option.value)}>
          {option.label}
        </Button>
      ))}
    </Pill>
  );
}
